import { useMemo } from "react";
import type { CatalogSort } from "../../../../api/configuration";
import { useEnabledItems } from "../../../../hooks/useMenu";
import { isLunchItem } from "../../../../lib/lunchCatalog";

// Подредбата на асортиментите/рецептите в каталога на „Маркиране" — една и
// съща за списъка (вътре във всяка група; групите остават по sortIndex) и за
// плоския грид. Личната настройка catalogSort избира между трите: „АБВ" (име),
// „КОД" (PLU код) и „СОРТ ИНДЕКС" (сорт индекс на продукта).

type SortableItem = { pluCode: number | null; sortIndex: number | null };

/**
 * PLU съвпаденията при търсене: по-късият код първи (точното съвпадение
 * изпреварва префиксните — при търсене „5" PLU 5 излиза преди PLU 500).
 */
export const byPluMatch = (a: SortableItem, b: SortableItem) =>
  String(a.pluCode).length - String(b.pluCode).length ||
  Number(a.pluCode) - Number(b.pluCode);

/**
 * Компаратор за каталога по личната настройка:
 * - "alphabetical" — по името на текущия език (localeCompare);
 * - "plu" — по PLU код нагоре; продаваемите БЕЗ код остават накрая, по име;
 * - "sort_index" — по сорт индекс нагоре, при еднакъв — по име; БЕЗ сорт
 *   индекс — накрая, по име.
 */
export const catalogComparator = <T extends SortableItem>(
  sort: CatalogSort,
  nameOf: (item: T) => string,
  language: string,
) => {
  const byName = (a: T, b: T) =>
    nameOf(a).localeCompare(nameOf(b), language);
  if (sort === "sort_index") {
    return (a: T, b: T) =>
      (a.sortIndex ?? Number.MAX_SAFE_INTEGER) -
        (b.sortIndex ?? Number.MAX_SAFE_INTEGER) || byName(a, b);
  }
  if (sort !== "plu") return byName;
  return (a: T, b: T) => {
    if (a.pluCode === null || b.pluCode === null) {
      if (a.pluCode === b.pluCode) return byName(a, b);
      return a.pluCode === null ? 1 : -1;
    }
    return a.pluCode - b.pluCode || byName(a, b);
  };
};

/**
 * Подредбите, между които превърта бутонът във футъра (OrderFooter): „АБВ"
 * винаги; „КОД" — само ако поне един активен артикул има PLU код; „СОРТ ИНДЕКС" —
 * само ако поне един има сорт индекс. Без нито едното подредбите са еднакви
 * и бутонът не се показва (списъкът е само с „АБВ"). Копията от обедното меню
 * не се броят за сорт индекса — те винаги носят поредния си номер в менюто
 * (lib/lunchCatalog.ts), иначе бутонът би излизал само по време на обяда.
 */
export const useCatalogSortOptions = (): CatalogSort[] => {
  const items = useEnabledItems();
  return useMemo(() => {
    const options: CatalogSort[] = ["alphabetical"];
    if (items.some((item) => item.pluCode !== null)) options.push("plu");
    if (items.some((item) => item.sortIndex !== null && !isLunchItem(item)))
      options.push("sort_index");
    return options;
  }, [items]);
};

/** Следващата подредба при клик на бутона (след последната — отначало). */
export const nextCatalogSort = (
  current: CatalogSort,
  options: CatalogSort[],
): CatalogSort => options[(options.indexOf(current) + 1) % options.length];
